import React, { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Paper,
  Container,
  Box,
} from '@mui/material';
import { MultiStepperProps } from './types';
import {
  StepperContainer,
  StepperSidebar,
  StepperContent,
  CompletionContainer,
  ProgressBarContainer,
  ProgressBar,
  ProgressSegment,
  ProgressBarContent,
} from './styles';
import { Button } from '../../atoms/Button';

export const MultiStepper: React.FC<MultiStepperProps> = ({
  steps,
  activeStep = 0,
  onStepChange,
  variant = 'vertical-sidebar',
  vertical = true,
  activeColor = '#f86400',
  completionContent,
  allowStepClick = false,
  alertContent,
  maxWidth = 'lg',
  showNavigation = true,
  nextLabel = 'Next',
  backLabel = 'Back',
  nextDisabled,
  backDisabled,
  onNext,
  onBack,
  renderNavigation,
}) => {
  const [navBusy, setNavBusy] = useState(false);

  const handleStepClick = (stepIndex: number) => {
    if (allowStepClick && onStepChange) {
      onStepChange(stepIndex);
    }
  };

  const isCompleted = activeStep >= steps.length;
  const currentStep = steps[activeStep];

  const resolvedNextLabel = currentStep?.nextLabel ?? nextLabel;
  const resolvedBackLabel = currentStep?.backLabel ?? backLabel;
  const resolvedNextDisabled = Boolean(
    navBusy || currentStep?.nextDisabled || nextDisabled,
  );
  const resolvedBackDisabled = Boolean(
    navBusy || currentStep?.backDisabled || backDisabled || activeStep === 0,
  );
  const isSubmitButton = Boolean(currentStep?.formId);

  const handleBackClick = async () => {
    if (navBusy) return;
    const backHandler = currentStep?.onBack || onBack;

    if (backHandler) {
      setNavBusy(true);
      try {
        await backHandler();
      } finally {
        setNavBusy(false);
      }
      return;
    }

    if (onStepChange && activeStep > 0) {
      onStepChange(activeStep - 1);
    }
  };

  const handleNextClick = async () => {
    if (navBusy) return;
    const nextHandler = currentStep?.onNext || onNext;

    if (nextHandler) {
      setNavBusy(true);
      try {
        await nextHandler();
      } finally {
        setNavBusy(false);
      }
      return;
    }

    if (onStepChange && activeStep < steps.length) {
      onStepChange(activeStep + 1);
    }
  };

  const renderNavigationSection = () => {
    if (!showNavigation || isCompleted || currentStep?.hideNavigation) return null;

    const navProps = {
      onNext: handleNextClick,
      onBack: handleBackClick,
      nextLabel: resolvedNextLabel,
      backLabel: resolvedBackLabel,
      nextDisabled: resolvedNextDisabled,
      backDisabled: resolvedBackDisabled,
      isSubmitButton,
      formId: currentStep?.formId,
    };

    if (renderNavigation) {
      return renderNavigation(navProps);
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button
            variant="outline"
            size="default"
            onClick={navProps.onBack}
            disabled={navProps.backDisabled}
          >
            {navProps.backLabel}
          </Button>
          <Button
            variant="default"
            size="default"
            type={navProps.isSubmitButton ? 'submit' : 'button'}
            form={navProps.isSubmitButton ? navProps.formId : undefined}
            onClick={!navProps.isSubmitButton ? navProps.onNext : undefined}
            disabled={navProps.nextDisabled}
          >
            {navProps.nextLabel}
          </Button>
        </Box>
      </Box>
    );
  };

  // Progress Bar Variant
  if (variant === 'progress') {
    return (
      <Container maxWidth={maxWidth}>
        <ProgressBarContent>
          <ProgressBarContainer>
            <ProgressBar>
              {steps.map((_, index) => (
                <ProgressSegment 
                  key={index} 
                  $active={activeStep >= index} 
                  $activeColor={activeColor}
                />
              ))}
            </ProgressBar>
          </ProgressBarContainer>

          <Box>
            {alertContent && (
              <Box sx={{ mb: 3 }}>
                {alertContent}
              </Box>
            )}

            {isCompleted ? (
              <CompletionContainer>
                {completionContent || (
                  <Box>
                    <Box component="h2" sx={{ mb: 2 }}>
                      All Steps Completed!
                    </Box>
                    <Box component="p">
                      You have successfully completed all steps.
                    </Box>
                  </Box>
                )}
              </CompletionContainer>
            ) : (
              <Box>
                {steps[activeStep]?.content}
                {renderNavigationSection()}
              </Box>
            )}
          </Box>
        </ProgressBarContent>
      </Container>
    );
  }

  // Vertical Sidebar Variant (default)
  return (
    <Container maxWidth={maxWidth}>
      <StepperContainer>
        <StepperSidebar>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stepper activeStep={activeStep} orientation={vertical ? 'vertical' : 'horizontal'}>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      step.optional ? (
                        <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          Optional
                        </Box>
                      ) : undefined
                    }
                    StepIconProps={{
                      sx: {
                        '&.Mui-active': { color: activeColor },
                        '&.Mui-completed': { color: activeColor },
                        cursor: allowStepClick ? 'pointer' : 'default',
                      },
                      onClick: () => handleStepClick(index),
                    }}
                    sx={{
                      cursor: allowStepClick ? 'pointer' : 'default',
                    }}
                    onClick={() => handleStepClick(index)}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </StepperSidebar>

        <StepperContent>
          <Paper sx={{ p: 3 }}>
            {alertContent && (
              <Box sx={{ mb: 3 }}>
                {alertContent}
              </Box>
            )}

            {isCompleted ? (
              <CompletionContainer>
                {completionContent || (
                  <Box>
                    <Box component="h2" sx={{ mb: 2 }}>
                      All Steps Completed!
                    </Box>
                    <Box component="p">
                      You have successfully completed all steps.
                    </Box>
                  </Box>
                )}
              </CompletionContainer>
            ) : (
              <Box>
                {steps[activeStep]?.content}
                {renderNavigationSection()}
              </Box>
            )}
          </Paper>
        </StepperContent>
      </StepperContainer>
    </Container>
  );
};

export type { MultiStepperProps, Step } from './types';
